import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import {
  getEventState,
  getEventWeeks,
  type EventPhase,
  type EventWeek,
  type EventWeekRules,
} from '../lib/api/event';
import { useViewerCountry } from '../lib/geo';
import { FREE_PLAY_WEEK } from '../lib/eventState';
import { WEEKLY_RULES_CONTENT } from '../content/weeklyRules';
import { HeadingUnderscore } from '../components/register/RegisterLayout';
import LeaderboardTabs from '../components/LeaderboardTabs';
import { Button } from '../components/Button';
import { FormError } from '../components/form/FormError';
import { NonUsEligibilityNotice } from '../components/NonUsEligibilityNotice';

/**
 * /rules — public contest rules viewer.
 *
 * Owns:
 *  - event-state fetch (phase + currentWeek) and weeks list
 *  - week selector (reuses LeaderboardTabs); visible weeks are those with
 *    published rules whose weekNumber ≤ currentWeek (i.e. weeks that have
 *    aired). Post-event currentWeek is the final week so all aired weeks
 *    surface naturally.
 *  - non-US viewer notice (yellow band) gated by useViewerCountry()
 *  - per-week rules body (verbatim transcription) from content/weeklyRules.tsx
 *
 * Layout: deep-brand-color outer section (no players.png overlay used by
 * RegisterLayout) + an Infoback card with the lilac gradient + diagonal
 * pattern. The card grows to fit content; the page scrolls naturally.
 */
/** The Free Play week (6) is withheld from the /rules tab strip until we've
 *  actually reached it. Contest weeks (1-5) are always visible. */
function isWeekVisible(weekNumber: number, currentWeek: number | null): boolean {
  if (weekNumber < FREE_PLAY_WEEK) return true;
  return currentWeek !== null && currentWeek >= FREE_PLAY_WEEK;
}

export default function RulesPage() {
  const reduce = useReducedMotion();
  const { country } = useViewerCountry();

  const [phase, setPhase] = useState<EventPhase | null>(null);
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [weeks, setWeeks] = useState<EventWeek[] | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    applyPageMeta(PAGE_META.rules);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getEventState(), getEventWeeks()])
      .then(([state, weeksRes]) => {
        if (cancelled) return;
        setPhase(state.phase);
        setCurrentWeek(state.currentWeek);
        setWeeks(weeksRes.weeks);

        // Default selection: the current week if it has a rules row,
        // otherwise the highest-numbered week that does. Unpublished
        // weeks remain selectable — the body shows a placeholder. The
        // Free Play week (6) only enters the visible set once we've reached
        // it (currentWeek >= 6), so it never surfaces during the contest.
        const visible = weeksRes.weeks
          .filter((w) => w.rules !== null && isWeekVisible(w.weekNumber, state.currentWeek))
          .map((w) => w.weekNumber);
        const fallback = visible[visible.length - 1] ?? null;
        const preferred = visible.includes(state.currentWeek) ? state.currentWeek : fallback;
        setSelectedWeek(preferred);
      })
      .catch(() => {
        if (cancelled) return;
        setBootError("We couldn't load the official rules right now. Please refresh and try again.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Every week that has a rules row is a tab — including unpublished ones.
  // The published flag gates the BODY content, not tab visibility, so users
  // see the full 5-week strip and clicking an unpublished week reveals a
  // "come back later" notice. To remove a week entirely from the strip,
  // delete its weekly_rules row. Exception: the Free Play week (6) is
  // withheld until currentWeek >= 6 so it never appears during the contest.
  const visibleWeeks = useMemo(() => {
    if (!weeks) return [];
    return weeks
      .filter((w) => w.rules !== null && isWeekVisible(w.weekNumber, currentWeek))
      .map((w) => w.weekNumber)
      .sort((a, b) => a - b);
  }, [weeks, currentWeek]);

  const selectedRules = useMemo<EventWeekRules | null>(() => {
    if (!weeks || selectedWeek === null) return null;
    return weeks.find((w) => w.weekNumber === selectedWeek)?.rules ?? null;
  }, [weeks, selectedWeek]);

  const showNonUsNotice = country !== null && country !== 'US';

  return (
    <section className="relative isolate flex w-full flex-1 flex-col items-center overflow-hidden px-[28px] pb-[60px] pt-[60px] sm:px-[40px] sm:pt-[80px] md:px-[64px]">
      <div className="relative flex w-full max-w-[1030px] flex-col gap-[20px]">
        <h1 className="font-['Orbitron',sans-serif] text-[clamp(28px,7vw,46px)] uppercase leading-[0.95] tracking-[-0.03em] text-white">
          rules
          <HeadingUnderscore />
        </h1>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative min-h-[450px] overflow-hidden"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #413850 0%, #8c05ea 100%), url('/assets/patterns/diagonal.svg')",
            backgroundBlendMode: 'overlay',
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.16]"
            style={{
              backgroundImage: "url('/assets/patterns/diagonal.svg')",
              backgroundRepeat: 'repeat',
            }}
          />

          <div className="relative flex min-h-[450px] flex-col items-stretch gap-[30px] px-[20px] py-[40px] sm:px-[60px] md:px-[100px]">
            {showNonUsNotice && <NonUsEligibilityNotice />}

            {bootError && <FormError>{bootError}</FormError>}

            {/* Loading state — phase is null until the event-state + weeks
                fetch resolves. Without this, slow connections would show
                only the Home button (no tabs, no body) and read as broken. */}
            {!bootError && phase === null && <RulesLoading />}

            {!bootError && phase !== null && visibleWeeks.length > 0 && selectedWeek !== null && (
              <>
                <LeaderboardTabs
                  weeks={visibleWeeks}
                  activeWeek={selectedWeek}
                  onChange={setSelectedWeek}
                  labelFor={(w) => (w >= FREE_PLAY_WEEK ? 'Free Play' : `Week ${w}`)}
                />
                <RulesBody weekNumber={selectedWeek} rules={selectedRules} />
              </>
            )}

            {!bootError && phase !== null && visibleWeeks.length === 0 && (
              <p className="text-center font-['Arial',sans-serif] text-[16px] leading-[1.4] text-white/85">
                Official rules will be published here before Week 1 airs.
              </p>
            )}

            <div className="mt-auto flex justify-center pt-[10px]">
              <Button variant="dark" to="/" className="w-[200px]">
                Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Loading state ────────────────────────────────────────────────── */

/**
 * Shown while the /event/state + /event/weeks fetches are in flight.
 * Fills the card's vertical space (the parent has min-h-[450px]) and
 * centers an Orbiton-styled spinner + "Loading official rules…" label so
 * a slow connection doesn't render an empty card with just the Home
 * button visible.
 */
function RulesLoading() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-1 flex-col items-center justify-center gap-[14px] py-[40px] text-white"
    >
      <span
        aria-hidden="true"
        className="block h-[36px] w-[36px] animate-spin rounded-full border-[3px] border-white/25 border-t-white"
      />
      <p className="font-['Arial',sans-serif] text-[14px] leading-[1.2] text-white/80">
        Loading official rules&hellip;
      </p>
    </div>
  );
}

/* ─── Rules body ───────────────────────────────────────────────────── */

type RulesBodyProps = {
  weekNumber: number;
  rules: EventWeekRules | null;
};

/**
 * Renders one of three states:
 *   - rules row missing entirely       → "rules not available" fallback
 *   - not visible yet                  → "come back later" placeholder. This is
 *     gated by the admin `published` flag (toggled in the admin Catalog →
 *     Weekly rules, no deploy). Weeks 2-5 show this because their row is
 *     unpublished — NOT because of any hardcoded per-week message. The same
 *     placeholder also covers the misconfiguration where a week is flipped
 *     published before its body content has shipped (see below), so there is no
 *     separate hardcoded notice that could mask the admin setting.
 *   - published + body content exists   → the verbatim rules transcription
 *     (web-side content keyed by week; see content/weeklyRules.tsx)
 *
 * Body content lives in code (content/weeklyRules.tsx) while the publish flag
 * lives in the DB, so revealing a new week is a two-step ship: deploy the
 * transcription, then flip `published`. If `published` is true but no body is
 * registered, we show the same placeholder and log a dev warning rather than
 * crash or invent a different message.
 *
 * Keyed on weekNumber so the list remounts on tab switch — keeps any
 * future entrance animation honest.
 */
function RulesBody({ weekNumber, rules }: RulesBodyProps) {
  const weekLabel = weekNumber >= FREE_PLAY_WEEK ? 'Free Play' : `Week ${weekNumber}`;
  if (!rules) {
    return (
      <div key={weekNumber} className="w-full text-center font-['Arial',sans-serif] text-[16px] leading-[1.4] text-white/85">
        <h2 className="sr-only">{weekLabel} rules</h2>
        <p>Rules for this week aren&rsquo;t available right now.</p>
      </div>
    );
  }

  const Content = WEEKLY_RULES_CONTENT[weekNumber];

  // Visibility is the admin `published` flag AND the body actually existing.
  // Either gate failing → the one placeholder, so what a visitor sees is driven
  // by the publish toggle, not a per-week hardcoded string.
  if (!rules.published || !Content) {
    if (rules.published && !Content) {
      // eslint-disable-next-line no-console
      console.warn(
        `[GH] Week ${weekNumber} rules are published but no body content is registered in content/weeklyRules.tsx — showing the come-back-later placeholder. Deploy the transcription before flipping published.`,
      );
    }
    return (
      <div
        key={weekNumber}
        className="w-full rounded-[10px] border border-white/15 bg-white/[0.04] px-[20px] py-[28px] text-center font-['Arial',sans-serif] text-[clamp(14px,3.4vw,16px)] leading-[1.4] text-white"
      >
        <h2 className="sr-only">{weekLabel} rules</h2>
        <p>Please come back later to view the {weekLabel} rules.</p>
      </div>
    );
  }

  return (
    <div key={weekNumber} className="w-full">
      <h2 className="sr-only">{weekLabel} official rules</h2>
      <Content />
    </div>
  );
}
